import React from 'react';
import { Maybe } from 'graphql/jsutils/Maybe';
import styled from 'styled-components';
import { Box, IconButton, createButtonColorConfig } from '@99designs/emu';
import { colors, spaces, typography } from '@99designs/emu/settings';
import { formatMessage } from '@99designs/frontend-components/i18n';
import { IconLeft4, IconRight4 } from '@99designs/icons';
import { DesignPlaceholder } from '@99designs/spa-common';
import { ImageSource } from '../../ProfileOverlay';
import Img from './Img';
import Paginator from './Paginator';

const Wrapper = styled.div`
    isolation: isolate;
`;

const DesignCount = styled.span`
    position: absolute;
    top: ${spaces['0.75bu']};
    right: ${spaces['0.75bu']};
    z-index: 1;
    color: white;
    background-color: rgba(0, 0, 0, 0.5);
    border-radius: 19px;
    padding: ${spaces['0.25bu']};
    width: 40px;
    text-align: center;
    font-size: ${typography.typeScale.size1}px;
`;

interface Props {
    displayName: string;
    designs: { title?: Maybe<string>; imageSources: ImageSource }[];
    showPaginators?: boolean;
    isClickable: boolean;
    onClick: () => void;
    onChange: (designIndex: number) => void;
    selectedIndex: number;
}

const Content = styled.div`
    position: relative;
    width: 100%;
    padding-bottom: 100%;
    background-color: ${colors.grey100};
`;

const Viewport = styled.div`
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    overflow: hidden;
`;

const Items = styled.div<{ selectedIndex: number }>`
    width: 100%;
    height: 100%;
    transition: transform 400ms ease-in-out;
    transform: ${({ selectedIndex }) => `translateX(calc(-${selectedIndex} * 100%))`};
`;

const Item = styled.div<{ idx: number }>`
    position: absolute;
    height: 100%;
    width: 100%;
    transform: ${({ idx }) => `translateX(calc(${idx} * 100%))`};
`;

const Control = styled.div<{ clickable: boolean }>`
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    z-index: 2;
    display: grid;
    grid-template-columns: 68px 1fr 68px;
    grid-template-areas: 'prev . next';
    opacity: 0;
    transition: opacity 250ms;
    cursor: ${({ clickable }) => (clickable ? 'pointer' : 'initial')};

    &:hover,
    &:focus {
        opacity: 1;
        transition: opacity 250ms;
    }
`;

const RoundNavigationButton = styled(IconButton)`
    margin: 0 12px;
`;

const ControlButtonWrapper = styled.div`
    align-self: center;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const Prev = styled(ControlButtonWrapper)`
    grid-area: prev;
`;

const Next = styled(ControlButtonWrapper)`
    grid-area: next;
`;

function DesignCarousel({
    designs,
    displayName,
    isClickable,
    onChange,
    onClick,
    selectedIndex,
    showPaginators,
}: Props) {
    const hasPrev = () => selectedIndex > 0;

    const hasNext = () => selectedIndex < designs.length - 1;

    const prev = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        if (hasPrev()) {
            onChange(selectedIndex - 1);
        }
    };

    const next = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        if (hasNext()) {
            onChange(selectedIndex + 1);
        }
    };

    if (designs.length === 0) {
        return (
            <Wrapper>
                <Content>
                    <Viewport>
                        <DesignPlaceholder size="large" icon={'design-1'}>
                            {__('No designs available')}
                        </DesignPlaceholder>
                    </Viewport>
                </Content>
                {showPaginators && (
                    <Box margin="1bu">
                        <Paginator
                            steps={designs.length}
                            selectedIndex={selectedIndex}
                            onSelectIndex={(selectedIndex) => onChange(selectedIndex)}
                        />
                    </Box>
                )}
            </Wrapper>
        );
    }

    return (
        <Wrapper>
            <DesignCount>
                {selectedIndex + 1}/{designs.length}
            </DesignCount>
            <Content>
                <Control
                    clickable={isClickable}
                    onClick={onClick}
                    aria-label={__('View designs')}
                    data-testid="design-carousel"
                    role="button"
                >
                    {hasPrev() && (
                        <Prev>
                            <RoundNavigationButton
                                noTooltip
                                aria-label="Previous (left arrow)"
                                icon={IconLeft4}
                                onClick={prev}
                                size="medium"
                                appearance={createButtonColorConfig({
                                    normal: {
                                        backgroundColorHover: 'grey700',
                                    },
                                })}
                            />
                        </Prev>
                    )}
                    {hasNext() && (
                        <Next>
                            <RoundNavigationButton
                                noTooltip
                                aria-label="Next (right arrow)"
                                icon={IconRight4}
                                onClick={next}
                                size="medium"
                                appearance={createButtonColorConfig({
                                    normal: {
                                        backgroundColorHover: 'grey700',
                                    },
                                })}
                            />
                        </Next>
                    )}
                </Control>
                <Viewport>
                    <Items selectedIndex={selectedIndex}>
                        {designs.map((design, idx) => (
                            <Item idx={idx} key={idx}>
                                <Img
                                    src={design.imageSources.mediaCrop}
                                    alt={
                                        design.title ??
                                        formatMessage(__('Design by {displayName}'), {
                                            displayName: displayName,
                                        })
                                    }
                                    key={idx}
                                    preload={idx > 0}
                                    style={{ height: '100%', width: 'auto' }}
                                />
                            </Item>
                        ))}
                    </Items>
                </Viewport>
            </Content>
            {showPaginators && (
                <Box margin="1bu">
                    <Paginator
                        steps={designs.length}
                        selectedIndex={selectedIndex}
                        onSelectIndex={(selectedIndex) => onChange(selectedIndex)}
                    />
                </Box>
            )}
        </Wrapper>
    );
}

export default DesignCarousel;
